import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Flex,
  Text,
  Button,
  Select,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  useToast,
  VStack,
  HStack,
  IconButton,
  useColorModeValue,
  Alert,
  AlertIcon,
  Tooltip,
  Textarea,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon, ViewIcon } from "@chakra-ui/icons";
import { FiRefreshCw, FiZap, FiRotateCcw, FiFileText } from "react-icons/fi";
import { releaseService, projectService } from "../services/edosService";
import api from "../services/api";

const ENV_COLORS = {
  dev: "blue",
  qa: "orange",
  uat: "purple",
  staging: "yellow",
  production: "green",
};
const DEPLOY_STATUS_COLORS = {
  pending: "gray",
  deploying: "blue",
  deployed: "green",
  failed: "red",
  rolled_back: "orange",
};

export default function ReleasesPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const headerBg = useColorModeValue("gray.50", "gray.700");

  const [projects, setProjects] = useState([]);
  const [releases, setReleases] = useState([]);
  const [projectTickets, setProjectTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState("");

  const fetchProjects = useCallback(async () => {
    try {
      const res = await projectService.list();
      setProjects(res.data.data || res.data.projects || []);
    } catch (_) {}
  }, []);

  const fetchReleases = useCallback(async () => {
    if (!selectedProjectId) return;
    setLoading(true);
    try {
      const res = await releaseService.listByProject(selectedProjectId);
      setReleases(res.data.data || res.data.releases || []);
    } catch (err) {
      toast({ title: "Failed to load releases", status: "error" });
    } finally {
      setLoading(false);
    }
  }, [selectedProjectId, toast]);

  const normalizeTicketRows = (payload) => {
    if (!payload) return [];
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.data?.tickets)) return payload.data.tickets;
    if (Array.isArray(payload?.tickets)) return payload.tickets;
    return [];
  };

  const fetchProjectTickets = useCallback(async () => {
    if (!selectedProjectId) {
      setProjectTickets([]);
      return;
    }
    setTicketsLoading(true);
    try {
      const allRows = [];
      let page = 1;
      let totalPages = 1;

      do {
        const res = await api.get("/api/tickets", {
          params: {
            page,
            limit: 100,
            projectId: selectedProjectId,
            _viewMode: "all",
            sortBy: "createdAt",
            sortOrder: "desc",
          },
        });

        const payload = res?.data;
        allRows.push(...normalizeTicketRows(payload));
        totalPages = Number(
          payload?.totalPages || payload?.data?.totalPages || 1,
        );
        page += 1;
      } while (page <= totalPages && page <= 50);

      setProjectTickets(allRows);
    } catch {
      setProjectTickets([]);
      toast({ title: "Failed to load release tickets", status: "error" });
    } finally {
      setTicketsLoading(false);
    }
  }, [selectedProjectId, toast]);

  const ticketCountsByRelease = useMemo(() => {
    const counts = {};
    projectTickets.forEach((ticket) => {
      const rid = ticket?.releaseId?._id || ticket?.releaseId;
      if (!rid) return;
      counts[rid] = (counts[rid] || 0) + 1;
    });
    return counts;
  }, [projectTickets]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);
  useEffect(() => {
    fetchReleases();
  }, [fetchReleases]);

  useEffect(() => {
    fetchProjectTickets();
  }, [fetchProjectTickets]);

  const handleViewTickets = (release) => {
    navigate(`/edos/releases/${release._id}`);
  };

  const handleDeploy = async (id) => {
    if (!window.confirm("Trigger deployment for this release?")) return;
    try {
      await releaseService.deploy(id);
      toast({ title: "Deployment triggered", status: "success" });
      fetchReleases();
    } catch (err) {
      toast({
        title: err?.response?.data?.message || "Deploy failed",
        status: "error",
      });
    }
  };

  const handleRollback = async (release) => {
    const reason = window.prompt("Rollback reason:");
    if (!reason) return;
    try {
      await releaseService.rollback(release._id, { reason });
      toast({ title: "Rolled back", status: "success" });
      fetchReleases();
    } catch (err) {
      toast({
        title: err?.response?.data?.message || "Rollback failed",
        status: "error",
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this release?")) return;
    try {
      await releaseService.delete(id);
      toast({ title: "Release deleted", status: "success" });
      fetchReleases();
    } catch (err) {
      toast({
        title: err?.response?.data?.message || "Failed",
        status: "error",
      });
    }
  };

  return (
    <Box p={6}>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Text fontSize="2xl" fontWeight="bold">
            Releases
          </Text>
          <Text color="gray.500" fontSize="sm">
            Manage deployments and release pipeline
          </Text>
        </Box>
        <HStack>
          <IconButton
            icon={<FiRefreshCw />}
            variant="outline"
            aria-label="Refresh"
            onClick={() => {
              fetchReleases();
              fetchProjectTickets();
            }}
          />
          <Button
            leftIcon={<AddIcon />}
            colorScheme="green"
            onClick={() =>
              navigate(
                `/edos/releases/create${selectedProjectId ? `?projectId=${selectedProjectId}` : ""}`,
              )
            }
          >
            New Release
          </Button>
        </HStack>
      </Flex>

      <Flex gap={3} mb={4} align="center">
        <Text fontSize="sm" fontWeight="medium" whiteSpace="nowrap">
          Select Project:
        </Text>
        <Select
          placeholder="Choose a project..."
          value={selectedProjectId}
          onChange={(e) => setSelectedProjectId(e.target.value)}
          maxW="320px"
        >
          {projects.map((p) => (
            <option key={p._id} value={p._id}>
              {p.projectName}
            </option>
          ))}
        </Select>
      </Flex>

      {!selectedProjectId ? (
        <Alert status="info">
          <AlertIcon />
          Select a project to view its releases.
        </Alert>
      ) : (
        <Box
          bg={bg}
          border="1px"
          borderColor={borderColor}
          borderRadius="md"
          overflow="hidden"
        >
          {loading ? (
            <Flex justify="center" p={10}>
              <Spinner size="xl" color="green.500" />
            </Flex>
          ) : releases.length === 0 ? (
            <Alert status="info" m={4}>
              <AlertIcon />
              No releases yet.
            </Alert>
          ) : (
            <Table variant="simple" size="sm">
              <Thead bg={headerBg}>
                <Tr>
                  <Th>Release</Th>
                  <Th>Environment</Th>
                  <Th>Deploy Status</Th>
                  <Th>Planned Date</Th>
                  <Th>Tickets</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {releases.map((r) => (
                  <Tr key={r._id}>
                    <Td>
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="semibold" fontSize="sm">
                          {r.releaseName}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          {r.releaseCode}
                        </Text>
                      </VStack>
                    </Td>
                    <Td>
                      <Badge colorScheme={ENV_COLORS[r.environment] || "gray"}>
                        {r.environment}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge
                        colorScheme={
                          DEPLOY_STATUS_COLORS[r.deploymentStatus] || "gray"
                        }
                      >
                        {r.deploymentStatus}
                      </Badge>
                    </Td>
                    <Td>
                      <Text fontSize="sm">
                        {r.plannedReleaseDate
                          ? new Date(r.plannedReleaseDate).toLocaleDateString()
                          : "-"}
                      </Text>
                    </Td>
                    <Td>
                      <Badge colorScheme="blue">
                        {ticketCountsByRelease[r._id] || 0}
                      </Badge>
                    </Td>
                    <Td>
                      <HStack spacing={1}>
                        <Tooltip label="View Tickets">
                          <IconButton
                            size="xs"
                            icon={<ViewIcon />}
                            variant="ghost"
                            colorScheme="blue"
                            onClick={() => handleViewTickets(r)}
                          />
                        </Tooltip>
                        {r.deploymentStatus !== "deployed" && (
                          <Tooltip label="Deploy">
                            <IconButton
                              size="xs"
                              icon={<FiZap />}
                              variant="ghost"
                              colorScheme="green"
                              onClick={() => handleDeploy(r._id)}
                            />
                          </Tooltip>
                        )}
                        {r.deploymentStatus === "deployed" && (
                          <Tooltip label="Rollback">
                            <IconButton
                              size="xs"
                              icon={<FiRotateCcw />}
                              variant="ghost"
                              colorScheme="orange"
                              onClick={() => handleRollback(r)}
                            />
                          </Tooltip>
                        )}
                        <Tooltip label="Generate Notes">
                          <IconButton
                            size="xs"
                            icon={<FiFileText />}
                            variant="ghost"
                            colorScheme="blue"
                            onClick={() => navigate(`/edos/releases/${r._id}`)}
                          />
                        </Tooltip>
                        <Tooltip label="Delete">
                          <IconButton
                            size="xs"
                            icon={<DeleteIcon />}
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => handleDelete(r._id)}
                          />
                        </Tooltip>
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </Box>
      )}
    </Box>
  );
}
